/**
 * FSRS-5 (Free Spaced Repetition Scheduler, version 5) implementation.
 *
 * The algorithm models a memory with three variables:
 *   - Stability (S)       : days until retrievability decays to 90%.
 *   - Difficulty (D)      : 1..10, how hard the item is for this particular learner.
 *   - Retrievability (R)  : probability of recall right now, derived from S and elapsed time.
 *
 * On every review the learner supplies a grade (Again / Hard / Good / Easy); the model
 * updates D and S, and the next due date is the point in time where R falls to the
 * user's desired retention (default 0.9).
 *
 * This is a pure, dependency-free, deterministic implementation. It performs no I/O and
 * takes "now" as an argument so that it is fully testable. Interval fuzz (the random
 * +/-5% jitter Anki applies to spread review load) is deliberately NOT applied here --
 * it would make scheduling non-reproducible; see `applyFuzz` for an opt-in helper.
 */
/** Grade the learner gives after seeing the answer. */
export const Rating = {
    Again: 1,
    Hard: 2,
    Good: 3,
    Easy: 4
};
/** Lifecycle state of a single card. Mirrors the `CardState` enum in the Prisma schema. */
export const CardState = {
    /** Never studied. */
    New: "NEW",
    /** Studied for the first time, still inside the short-term learning steps. */
    Learning: "LEARNING",
    /** Graduated: scheduled in days by FSRS. */
    Review: "REVIEW",
    /** Was forgotten in review and is being re-learned through the short-term steps. */
    Relearning: "RELEARNING"
};
/**
 * The 19 FSRS-5 model weights. These are the defaults published with FSRS-5, fitted over a
 * large public review-log corpus. They are a reasonable prior for a learner with no history;
 * a per-user optimiser could replace them later without touching any other code.
 */
export const FSRS5_DEFAULT_WEIGHTS = [
    0.40255, // w0  initial stability after "Again"
    1.18385, // w1  initial stability after "Hard"
    3.173, //   w2  initial stability after "Good"
    15.69105, // w3  initial stability after "Easy"
    7.1949, //  w4  initial difficulty base
    0.5345, //  w5  initial difficulty curvature
    1.4604, //  w6  difficulty change per grade step
    0.0046, //  w7  difficulty mean-reversion strength
    1.54575, // w8  stability growth scale
    0.1192, //  w9  stability growth decay with S
    1.01925, // w10 stability growth sensitivity to forgetting risk
    1.9395, //  w11 post-lapse stability scale
    0.11, //    w12 post-lapse penalty from difficulty
    0.29605, // w13 post-lapse dependence on prior stability
    2.2698, //  w14 post-lapse sensitivity to retrievability
    0.2315, //  w15 "Hard" stability penalty
    2.9898, //  w16 "Easy" stability bonus
    0.51655, // w17 same-day (short-term) stability scale
    0.6621 //   w18 same-day (short-term) grade offset
];
/** Power-law forgetting curve exponent used by FSRS 4.5 and later. */
export const DECAY = -0.5;
/** Chosen so that R(t = S) == 0.9 exactly. */
export const FACTOR = 19 / 81;
const MIN_STABILITY = 0.01;
const MAX_STABILITY = 36500;
const MIN_DIFFICULTY = 1;
const MAX_DIFFICULTY = 10;
const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;
/** Short-term steps, in minutes, used while a card is in LEARNING / RELEARNING. */
export const LEARNING_STEPS_MINUTES = [1, 10];
export const RELEARNING_STEPS_MINUTES = [10];
export const DEFAULT_FSRS_CONFIG = {
    desiredRetention: 0.9,
    maximumIntervalDays: 3650,
    weights: FSRS5_DEFAULT_WEIGHTS
};
function clamp(value, min, max) {
    if (Number.isNaN(value))
        return min;
    return Math.min(Math.max(value, min), max);
}
function weightAt(weights, index) {
    const value = weights[index];
    if (value === undefined) {
        throw new Error(`FSRS weights array is too short: missing w${index}`);
    }
    return value;
}
/**
 * Probability of recalling a card `elapsedDays` after its last review.
 * Power-law curve: R(t) = (1 + FACTOR * t / S) ^ DECAY.
 */
export function retrievability(elapsedDays, stability) {
    if (stability <= 0)
        return 0;
    if (elapsedDays <= 0)
        return 1;
    return Math.pow(1 + (FACTOR * elapsedDays) / stability, DECAY);
}
/** Inverse of `retrievability`: days until recall probability drops to `desiredRetention`. */
export function intervalForRetention(stability, desiredRetention) {
    const retention = clamp(desiredRetention, 0.5, 0.995);
    return (stability / FACTOR) * (Math.pow(retention, 1 / DECAY) - 1);
}
/** Round a raw FSRS interval into a whole number of days within the configured bounds. */
export function roundInterval(rawDays, maximumIntervalDays) {
    return clamp(Math.round(rawDays), 1, maximumIntervalDays);
}
export function initialStability(weights, rating) {
    return clamp(weightAt(weights, rating - 1), MIN_STABILITY, MAX_STABILITY);
}
export function initialDifficulty(weights, rating) {
    const raw = weightAt(weights, 4) - Math.exp(weightAt(weights, 5) * (rating - 1)) + 1;
    return clamp(raw, MIN_DIFFICULTY, MAX_DIFFICULTY);
}
/**
 * Update difficulty after a review. Two effects compose here:
 *   1. a linear-damped step towards easier/harder based on the grade, and
 *   2. a slow mean reversion towards the difficulty of an "Easy" first answer,
 *      which stops difficulty from ratcheting to 10 and never coming back.
 */
export function nextDifficulty(weights, difficulty, rating) {
    const delta = -weightAt(weights, 6) * (rating - 3);
    const damped = difficulty + (delta * (10 - difficulty)) / 9;
    const reverted = weightAt(weights, 7) * initialDifficulty(weights, Rating.Easy) + (1 - weightAt(weights, 7)) * damped;
    return clamp(reverted, MIN_DIFFICULTY, MAX_DIFFICULTY);
}
/** Stability after a successful recall (Hard / Good / Easy). */
export function nextRecallStability(weights, difficulty, stability, r, rating) {
    const hardPenalty = rating === Rating.Hard ? weightAt(weights, 15) : 1;
    const easyBonus = rating === Rating.Easy ? weightAt(weights, 16) : 1;
    const growth = Math.exp(weightAt(weights, 8)) *
        (11 - difficulty) *
        Math.pow(stability, -weightAt(weights, 9)) *
        (Math.exp((1 - r) * weightAt(weights, 10)) - 1) *
        hardPenalty *
        easyBonus;
    return clamp(stability * (1 + growth), MIN_STABILITY, MAX_STABILITY);
}
/** Stability after a lapse (graded "Again" on a card that was in REVIEW). */
export function nextForgetStability(weights, difficulty, stability, r) {
    const raw = weightAt(weights, 11) *
        Math.pow(difficulty, -weightAt(weights, 12)) *
        (Math.pow(stability + 1, weightAt(weights, 13)) - 1) *
        Math.exp((1 - r) * weightAt(weights, 14));
    return clamp(raw, MIN_STABILITY, MAX_STABILITY);
}
/**
 * Stability update for a same-day review -- the FSRS-5 addition that lets repeated
 * attempts within a single session move the model instead of being discarded.
 */
export function nextShortTermStability(weights, stability, rating) {
    const raw = stability * Math.exp(weightAt(weights, 17) * (rating - 3 + weightAt(weights, 18)));
    return clamp(raw, MIN_STABILITY, MAX_STABILITY);
}
/** A brand-new, never-studied card, due immediately. */
export function newCard(now) {
    return {
        state: CardState.New,
        due: now,
        stability: 0,
        difficulty: 0,
        step: 0,
        reps: 0,
        lapses: 0,
        lastReview: null,
        scheduledDays: 0
    };
}
function addMinutes(from, minutes) {
    return new Date(from.getTime() + minutes * MINUTE_MS);
}
function addDays(from, days) {
    return new Date(from.getTime() + days * DAY_MS);
}
function stepMinutes(steps, index) {
    const last = steps[steps.length - 1] ?? 10;
    return steps[Math.min(index, steps.length - 1)] ?? last;
}
function resolveConfig(config) {
    return {
        desiredRetention: config?.desiredRetention ?? DEFAULT_FSRS_CONFIG.desiredRetention,
        maximumIntervalDays: config?.maximumIntervalDays ?? DEFAULT_FSRS_CONFIG.maximumIntervalDays,
        weights: config?.weights ?? DEFAULT_FSRS_CONFIG.weights
    };
}
/**
 * Apply one review to a card and return the updated scheduling plus the log row.
 *
 * The card is not mutated. `now` must be supplied by the caller so the whole scheduler
 * stays deterministic and testable.
 */
export function reviewCard(card, rating, now, config) {
    const { desiredRetention, maximumIntervalDays, weights } = resolveConfig(config);
    const elapsedMs = card.lastReview ? now.getTime() - card.lastReview.getTime() : 0;
    const elapsedDays = card.lastReview ? Math.max(0, elapsedMs / DAY_MS) : 0;
    const r = card.state === CardState.New ? 0 : retrievability(elapsedDays, card.stability);
    const log = {
        rating,
        stateBefore: card.state,
        stabilityBefore: card.stability,
        difficultyBefore: card.difficulty,
        elapsedDays,
        scheduledDays: 0,
        retrievability: r,
        reviewedAt: now
    };
    const next = {
        ...card,
        reps: card.reps + 1,
        lastReview: now
    };
    if (card.state === CardState.New) {
        // First ever exposure: seed the memory model from the grade alone.
        next.difficulty = initialDifficulty(weights, rating);
        next.stability = initialStability(weights, rating);
        if (rating === Rating.Easy) {
            // "Easy" on a brand-new card skips the learning steps entirely.
            const days = roundInterval(intervalForRetention(next.stability, desiredRetention), maximumIntervalDays);
            next.state = CardState.Review;
            next.step = 0;
            next.scheduledDays = days;
            next.due = addDays(now, days);
        }
        else {
            // Again -> first step, Hard -> first step, Good -> second step.
            next.state = CardState.Learning;
            next.step = rating === Rating.Good ? 1 : 0;
            next.scheduledDays = 0;
            next.due = addMinutes(now, stepMinutes(LEARNING_STEPS_MINUTES, next.step));
        }
    }
    else if (card.state === CardState.Learning || card.state === CardState.Relearning) {
        const steps = card.state === CardState.Learning ? LEARNING_STEPS_MINUTES : RELEARNING_STEPS_MINUTES;
        next.difficulty = nextDifficulty(weights, card.difficulty, rating);
        next.stability = nextShortTermStability(weights, card.stability, rating);
        if (rating === Rating.Again) {
            next.step = 0;
            next.scheduledDays = 0;
            next.due = addMinutes(now, stepMinutes(steps, 0));
        }
        else if (rating === Rating.Hard) {
            // Stay on the current step rather than advancing.
            next.scheduledDays = 0;
            next.due = addMinutes(now, stepMinutes(steps, card.step));
        }
        else if (rating === Rating.Good && card.step < steps.length - 1) {
            next.step = card.step + 1;
            next.scheduledDays = 0;
            next.due = addMinutes(now, stepMinutes(steps, next.step));
        }
        else {
            // Graduate out of the short-term steps into day-scale scheduling.
            const days = roundInterval(intervalForRetention(next.stability, desiredRetention), maximumIntervalDays);
            next.state = CardState.Review;
            next.step = 0;
            next.scheduledDays = days;
            next.due = addDays(now, days);
        }
    }
    else {
        // REVIEW: the interesting case, full FSRS update.
        next.difficulty = nextDifficulty(weights, card.difficulty, rating);
        if (rating === Rating.Again) {
            // A lapse. FSRS-5 takes the more conservative of the long-term post-lapse
            // stability and the same-day short-term stability.
            const longTerm = nextForgetStability(weights, card.difficulty, card.stability, r);
            const shortTerm = nextShortTermStability(weights, card.stability, rating);
            next.stability = Math.min(longTerm, shortTerm);
            next.state = CardState.Relearning;
            next.step = 0;
            next.lapses = card.lapses + 1;
            next.scheduledDays = 0;
            next.due = addMinutes(now, stepMinutes(RELEARNING_STEPS_MINUTES, 0));
        }
        else {
            next.stability = nextRecallStability(weights, card.difficulty, card.stability, r, rating);
            const days = roundInterval(intervalForRetention(next.stability, desiredRetention), maximumIntervalDays);
            next.state = CardState.Review;
            next.step = 0;
            next.scheduledDays = days;
            next.due = addDays(now, days);
        }
    }
    log.scheduledDays = next.scheduledDays;
    return { card: next, log };
}
/**
 * Compute what each of the four buttons would do, so the study UI can label them
 * ("Again 1m / Hard 10m / Good 3d / Easy 8d") before the learner commits.
 */
export function previewRatings(card, now, config) {
    return [Rating.Again, Rating.Hard, Rating.Good, Rating.Easy].map((rating) => {
        const { card: after } = reviewCard(card, rating, now, config);
        const deltaMs = after.due.getTime() - now.getTime();
        return {
            rating,
            days: after.scheduledDays,
            minutes: Math.max(1, Math.round(deltaMs / MINUTE_MS)),
            due: after.due,
            state: after.state
        };
    });
}
/**
 * Optional Anki-style interval fuzz, spreading same-day due clusters by up to ~5%.
 * Kept out of `reviewCard` so scheduling stays reproducible; call it explicitly with
 * an injected random source if you want load-spreading.
 */
export function applyFuzz(days, random) {
    if (days < 2.5)
        return days;
    const spread = Math.max(1, Math.round(days * 0.05));
    const offset = Math.round((random() * 2 - 1) * spread);
    return Math.max(1, days + offset);
}
/**
 * Map an objective quiz/exam answer onto an FSRS grade. Used by the multiple-choice
 * modes, where the learner never presses a self-assessment button. The thresholds are
 * deliberately generous -- an answer that was correct but slow is "Hard", not "Good".
 */
export function autoRate(isCorrect, elapsedMs, slowThresholdMs = 20_000) {
    if (!isCorrect)
        return Rating.Again;
    return elapsedMs > slowThresholdMs ? Rating.Hard : Rating.Good;
}
//# sourceMappingURL=fsrs.js.map